/**
 * account_ops.service.ts — 본인 계정 요청(다른 기기 로그아웃·비밀번호 변경 뒤 정리·내 기기 강제 로그아웃).
 *
 * 설계: docs/design/12_통합관리_계정기기설계.md §4-2 account_ops · §5-5 rules · §6-2 processAccountOp.
 * runAdminOp(admin_ops.service.ts)와 같은 Firestore 트리거 패턴(AGENTS §5 — onCall 금지):
 *   1. account_ops/{opId} 생성 { action, params, caller_uid, status:"pending", created_at }
 *   2. 함수 processAccountOp(onCreate)가 처리 → status "done"/"error" + result/error
 *   3. 여기서 onSnapshot 으로 결과를 기다린다
 *
 * params 계약(A 묶음 함수와 맞출 것):
 *   - self.logout_all        { keep_device_id?, hard? }  → { flagged, hard }  기본 소프트(현재 기기 유지), hard 면 현재 기기도 끊김
 *   - self.password_changed  { keep_device_id }          → { flagged }        토큰 폐기 없음, 다른 기기 플래그만
 *   - self.device_revoke     { device_id, mode: "logout"|"release" } → { mode } 내 다른 기기 하나(함수가 status:'revoked')
 */
import { addDoc, collection, onSnapshot, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebase";
import type { AccountOpAction } from "@/domain/session";

const OP_TIMEOUT_MS = 30_000;

/** 사용자에게 보일 오류 문구로 바꾼다 — rules·함수가 아직 배포되지 않은 경우를 따로 안내 */
export function accountOpErrorText(e: unknown): string {
  const code = (e as { code?: string })?.code ?? "";
  if (code === "permission-denied" || code === "firestore/permission-denied") {
    return "이 기능은 아직 서버에 준비되지 않았거나 권한이 없습니다. 관리자에게 문의하세요.";
  }
  if (code === "unavailable") return "서버에 연결할 수 없습니다. 네트워크를 확인하고 다시 시도하세요.";
  const msg = (e as Error)?.message;
  return msg || "요청을 처리하지 못했습니다. 잠시 후 다시 시도하세요.";
}

export function runAccountOp<T = Record<string, unknown>>(
  action: AccountOpAction,
  params: Record<string, unknown>,
): Promise<T> {
  const caller_uid = auth.currentUser?.uid;
  if (!caller_uid) return Promise.reject(new Error("로그인이 필요합니다."));

  return new Promise<T>((resolve, reject) => {
    let settled = false;
    let unsub: (() => void) | null = null;
    let timer = 0;

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      if (unsub) unsub();
      if (timer) window.clearTimeout(timer);
      fn();
    };

    addDoc(collection(db, "account_ops"), {
      action,
      params,
      caller_uid,
      status: "pending",
      created_at: serverTimestamp(),
    })
      .then((ref) => {
        timer = window.setTimeout(
          () =>
            finish(() =>
              reject(new Error("서버 응답이 없습니다. 잠시 후 다시 시도하세요. (처리 함수가 아직 배포되지 않았을 수 있습니다)")),
            ),
          OP_TIMEOUT_MS,
        );
        unsub = onSnapshot(
          ref,
          (snap) => {
            const d = snap.data();
            if (!d) return;
            if (d.status === "done") finish(() => resolve((d.result ?? null) as T));
            else if (d.status === "error") finish(() => reject(new Error((d.error as string) || "요청을 처리하지 못했습니다.")));
          },
          (e) => finish(() => reject(new Error(accountOpErrorText(e)))),
        );
      })
      .catch((e) => finish(() => reject(new Error(accountOpErrorText(e)))));
  });
}
